import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import StepOneLocationDetails from "./StepOneLocationDetails";
import StepTwoPlaceQuestions from "./StepTwoPlaceQuestions";
import StepThreeReview from "./StepThreeReview";
import "./EditSavedAddress.css";

const STEP_LABELS = [
  { id: 1, label: "تحديد الموقع" },
  { id: 2, label: "تفاصيل المكان" },
  { id: 3, label: "مراجعة وحفظ" },
];

function collectVisibleQuestionIds(baseQuestions, answers) {
  const ids = new Set();

  const visit = (q) => {
    if (!q || ids.has(q.id)) return;
    ids.add(q.id);

    const ans = answers[q.id];
    if (!ans) return;

    const selectedIds = Array.isArray(ans.question_option_ids)
      ? ans.question_option_ids
      : ans.question_option_id != null
      ? [ans.question_option_id]
      : [];
    if (!selectedIds.length) return;

    (q.options || []).forEach((opt) => {
      if (!selectedIds.includes(opt.id)) return;
      (opt.conditions || []).forEach((cond) => {
        if (cond.action === "show" && cond.target_question) {
          visit(cond.target_question);
        }
      });
    });
  };

  (baseQuestions || []).forEach(visit);
  return ids;
}

function flattenAllQuestions(baseQuestions) {
  const map = new Map();

  const visit = (q) => {
    if (!q || map.has(q.id)) return;
    map.set(q.id, q);
    (q.options || []).forEach((opt) => {
      (opt.conditions || []).forEach((cond) => {
        if (cond.target_question) visit(cond.target_question);
      });
    });
  };

  (baseQuestions || []).forEach(visit);
  return map;
}

function buildAnswersFromQuestions(list) {
  const result = {};

  const visit = (q) => {
    if (!q || result[q.id]) return;

    if (q.type === "multiple_choice") {
      const selectedOpts = (q.options || []).filter((o) => o.selected);
      if (selectedOpts.length) {
        const extra_texts = {};
        selectedOpts.forEach((o) => {
          if (o.requires_input && (q.answer_value || q.answer_text)) {
            extra_texts[o.id] = q.answer_value || q.answer_text;
          }
        });
        result[q.id] = {
          question_option_ids: selectedOpts.map((o) => o.id),
          extra_texts,
        };
      }
    } else if (q.type === "single_choice") {
      const selectedOpt = (q.options || []).find((o) => o.selected);
      if (selectedOpt) {
        const extra_texts = {};
        if (selectedOpt.requires_input && (q.answer_value || q.answer_text)) {
          extra_texts[selectedOpt.id] = q.answer_value || q.answer_text;
        }
        result[q.id] = { question_option_id: selectedOpt.id, extra_texts };
      }
    } else if (q.answer_value || q.answer_text) {
      result[q.id] = { answer_value: q.answer_value || q.answer_text };
    }

    (q.options || []).forEach((opt) => {
      (opt.conditions || []).forEach((cond) => {
        if (cond.target_question) visit(cond.target_question);
      });
    });
  };

  (list || []).forEach(visit);
  return result;
}

export default function EditSavedAddress() {
  const { savedAddressId, addressId } = useParams();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const token = user?.token;
  const baseUrl = "https://estbn.com/EstbnNew/public";

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // ---- Step 1 state ----
  const [addressName, setAddressName] = useState("");
  const [fullAddress, setFullAddress] = useState("");
  const [lat, setLat] = useState(30.0444);
  const [lng, setLng] = useState(31.2357);
  const [note, setNote] = useState("");
  const [existingImages, setExistingImages] = useState([]);
  const [newImages, setNewImages] = useState([]);

  const [addressTypes, setAddressTypes] = useState([]);
  const [selectedTypeId, setSelectedTypeId] = useState(null);
  const [fields, setFields] = useState([]);
  const [fieldValues, setFieldValues] = useState({});

  // ---- Step 2 state ----
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});

  useEffect(() => {
    fetchInitialData();
    fetchAddressTypes();
    // eslint-disable-next-line
  }, []);

  const fetchInitialData = async () => {
    try {
      const res = await axios.get(
        `${baseUrl}/api/portal/saved-addresses/${savedAddressId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data = res.data.data;
      console.log(data);
      const address = data.address;

      setAddressName(address?.name || "");
      setFullAddress(address?.address || "");
      if (address?.latitude) setLat(parseFloat(address.latitude));
      if (address?.longitude) setLng(parseFloat(address.longitude));
      const lastNote = address?.notes?.at(-1);
      setNote(lastNote?.content || "");
      setExistingImages(address?.images || []);
      const initialTypeId = address?.type?.id || null;
      setSelectedTypeId(initialTypeId);

      const fv = {};
      (address?.type?.fields || []).forEach((item) => {
        if (item?.key) fv[item.key] = item.value;
      });

       setQuestions(data.question_answers || []);
       setAnswers(buildAnswersFromQuestions(data.question_answers || []));

      if (initialTypeId) await fetchFields(initialTypeId, fv);
    } catch (error) {
      console.log(error.message);
    } finally {
      setLoading(false);
    }
  };

   const fetchAddressTypes = async () => {
    try {
      const res = await axios.get(
        `${baseUrl}/api/portal/saved-addresses/address-types`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const raw = res.data?.data;
      const list = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.data)
        ? raw.data
        : Array.isArray(raw?.types)
        ? raw.types
        : [];
      setAddressTypes(list);
    } catch (error) {
      console.log(error.message);
    }
  };

  const fetchFields = async (typeId, presetValues = {}) => {
    try {
      const res = await axios.get(
        `${baseUrl}/api/portal/saved-addresses/address-types/${typeId}/fields`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const newFields = res.data.data?.fields || [];
      setFields(newFields);
      setFieldValues((prev) => {
        const merged = {};
        newFields.forEach((f) => {
          merged[f.key] =
            presetValues[f.key] !== undefined
              ? presetValues[f.key]
              : prev[f.key] || "";
        });
        return merged;
      });
    } catch (error) {
      console.log(error.message);
    }
  };

  const refetchQuestionsForStepTwo = async () => {
    try {
      const res = await axios.get(
        `${baseUrl}/api/portal/saved-addresses/${savedAddressId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const freshQuestions = res.data?.data?.question_answers || [];
      setQuestions(freshQuestions);
      setAnswers(buildAnswersFromQuestions(freshQuestions));
    } catch (error) {
      console.log(error.message);
    }
  };

  const handleTypeSelect = (typeId) => {
    setSelectedTypeId(typeId);
    fetchFields(typeId);
  };

  const handleFieldChange = (key, value) => {
    setFieldValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveStepOne = async () => {
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("_method", "POST");
      formData.append("name", addressName);
      formData.append("type_id", selectedTypeId || "");
      formData.append("address_notes", note);
      formData.append("address", fullAddress);
      formData.append("latitude", lat);
      formData.append("longitude", lng);

      newImages.forEach((file) => formData.append("images[]", file));

      fields.forEach((f) => {
        formData.append(f.key, fieldValues[f.key] ?? "");
      });

      await axios.post(
        `${baseUrl}/api/portal/order/update-address/${addressId}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );
      await refetchQuestionsForStepTwo();
      setStep(2);
    } catch (error) {
      console.log(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAnswerSelect = (questionId, optionId) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: {
        question_option_id: optionId,
        extra_texts: prev[questionId]?.extra_texts || {},
      },
    }));
  };

  const handleMultiAnswerToggle = (question, option) => {
    setAnswers((prev) => {
      const current = prev[question.id]?.question_option_ids || [];
      let next;

      if (option.is_only) {
        next = current.includes(option.id) ? [] : [option.id];
      } else {
        const withoutOnlyOptions = current.filter((id) => {
          const opt = question.options.find((o) => o.id === id);
          return !opt?.is_only;
        });
        next = withoutOnlyOptions.includes(option.id)
          ? withoutOnlyOptions.filter((id) => id !== option.id)
          : [...withoutOnlyOptions, option.id];
      }

      return {
        ...prev,
        [question.id]: { ...prev[question.id], question_option_ids: next },
      };
    });
  };

  const handleExtraTextChange = (questionId, optionId, text) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        extra_texts: {
          ...(prev[questionId]?.extra_texts || {}),
          [optionId]: text,
        },
      },
    }));
  };

  const handleAnswerText = (questionId, text) => {
    setAnswers((prev) => ({ ...prev, [questionId]: { answer_value: text } }));
  };

    const handleTimeAnswerChange = (questionId, field, value) => {
      setAnswers((prev) => ({
        ...prev,
        [questionId]: {
          ...prev[questionId],
          answer_value: {
            ...(prev[questionId]?.answer_value || {}),
            [field]: value,
          },
        },
      }));
    };

  const handleSaveStepTwo = async () => {
    setSaving(true);
    try {
      const body = new FormData();
      body.append("_method", "POST");

      const visibleIds = collectVisibleQuestionIds(questions, answers);
      const questionsMap = flattenAllQuestions(questions);
      let i = 0;

      Object.entries(answers).forEach(([questionId, ans]) => {
        const qId = Number(questionId);
        if (!visibleIds.has(qId)) return;

        const questionDef = questionsMap.get(qId);

        if (Array.isArray(ans.question_option_ids)) {
          ans.question_option_ids.forEach((optId) => {
            body.append(`answers[${i}][question_id]`, qId);
            body.append(`answers[${i}][question_option_id]`, optId);
            const extra = ans.extra_texts?.[optId];
            if (extra) body.append(`answers[${i}][answer_value]`, extra);
            i++;
          });
        } else if (ans.question_option_id != null) {
          body.append(`answers[${i}][question_id]`, qId);
          body.append(
            `answers[${i}][question_option_id]`,
            ans.question_option_id,
          );
          const extra = ans.extra_texts?.[ans.question_option_id];
          if (extra) body.append(`answers[${i}][answer_value]`, extra);
          i++;
        } else if (
          questionDef?.type === "time" &&
          ans.answer_value &&
          typeof ans.answer_value === "object"
        ) {
          body.append(`answers[${i}][question_id]`, qId);
          if (ans.answer_value.from) {
            body.append(
              `answers[${i}][answer_value][from]`,
              ans.answer_value.from,
            );
          }
          if (ans.answer_value.to) {
            body.append(`answers[${i}][answer_value][to]`, ans.answer_value.to);
          }
          i++;
        } else if (ans.answer_value) {
          body.append(`answers[${i}][question_id]`, qId);
          body.append(`answers[${i}][answer_value]`, ans.answer_value);
          i++;
        }
      });

      await axios.post(
        `${baseUrl}/api/portal/order/update-address-answers/${savedAddressId}`,
        body,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );
      setStep(3);
    } catch (error) {
      console.log(error.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="wizard-loading d-flex justify-content-center align-items-center">
        <div className="spinner-border text-dark" role="status" />
      </div>
    );
  }

  return (
    <div className="wizard-page p-4">
      <div className=" d-flex flex-column justify-content-around align-items-start">
        <h2 className="wizard-title fw-bold mb-1">Edit Address</h2>
        <p className="text-muted mb-4">
          عدّل بيانات العنوان علشان فريق الخدمة يوصلك بسهولة
        </p>
      </div>
      <div className="wizard-stepper d-flex align-items-center mb-4">
        {STEP_LABELS.map((s, idx) => (
          <React.Fragment key={s.id}>
            <div className="d-flex flex-column align-items-center wizard-step-item">
              <div
                className={`wizard-step-circle ${
                  step === s.id ? "active" : step > s.id ? "completed" : ""
                }`}
              >
                {step > s.id ? <i className="fa-solid fa-check"></i> : s.id}
              </div>
              <span
                className={`wizard-step-label mt-2 ${
                  step === s.id ? "fw-semibold text-dark" : "text-muted"
                }`}
              >
                {s.label}
              </span>
            </div>
            {idx < STEP_LABELS.length - 1 && (
              <div
                className={`wizard-step-line ${step > s.id ? "completed" : ""}`}
              ></div>
            )}
          </React.Fragment>
        ))}
      </div>

      {step === 1 && (
        <StepOneLocationDetails
          addressName={addressName}
          setAddressName={setAddressName}
          fullAddress={fullAddress}
          setFullAddress={setFullAddress}
          lat={lat}
          lng={lng}
          setLat={setLat}
          setLng={setLng}
          note={note}
          setNote={setNote}
          existingImages={existingImages}
          newImages={newImages}
          setNewImages={setNewImages}
          addressTypes={addressTypes}
          selectedTypeId={selectedTypeId}
          onTypeSelect={handleTypeSelect}
          fields={fields}
          fieldValues={fieldValues}
          onFieldChange={handleFieldChange}
          onNext={handleSaveStepOne}
          onCancel={() => navigate(-1)}
          saving={saving}
        />
      )}

      {step === 2 && (
        <StepTwoPlaceQuestions
          addressName={addressName}
          fullAddress={fullAddress}
          questions={questions}
          answers={answers}
          onAnswerSelect={handleAnswerSelect}
          onMultiAnswerToggle={handleMultiAnswerToggle}
          onExtraTextChange={handleExtraTextChange}
          onAnswerText={handleAnswerText}
          onTimeChange={handleTimeAnswerChange}
          onBack={() => setStep(1)}
          onNext={handleSaveStepTwo}
          onEditLocation={() => setStep(1)}
          saving={saving}
        />
      )}

      {step === 3 && (
        <StepThreeReview
          addressName={addressName}
          fullAddress={fullAddress}
          addressTypes={addressTypes}
          selectedTypeId={selectedTypeId}
          fields={fields}
          fieldValues={fieldValues}
          note={note}
          questions={questions}
          answers={answers}
          onBack={() => setStep(2)}
          onDone={() => navigate(-1)}
        />
      )}
    </div>
  );
}
